import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework4Question.css';

const Homework5Question2: React.FC = () => {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [filteredImage, setFilteredImage] = useState<string | null>(null);
  const [filterVisualization, setFilterVisualization] = useState<string | null>(null);
  const [spectrumImage, setSpectrumImage] = useState<string | null>(null);
  
  const [filterType, setFilterType] = useState<'notch' | 'band'>('notch');
  const [D0, setD0] = useState<number>(10);
  const [order, setOrder] = useState<number>(4);
  const [bandWidth, setBandWidth] = useState<number>(20);
  const [autoDetect, setAutoDetect] = useState<boolean>(true);
  const [manualNotchCenters, setManualNotchCenters] = useState<Array<[number, number]>>([]);
  const [detectedCenters, setDetectedCenters] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-load sample image on component mount
  useEffect(() => {
    const loadSampleImage = async () => {
      try {
        const response = await fetch('/sample-images/HW5_2.jpg');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = (e) => {
          setOriginalImage(e.target?.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Failed to load sample image:', err);
      }
    };
    loadSampleImage();
  }, []);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        // Clear previous results
        setFilteredImage(null);
        setFilterVisualization(null);
        setSpectrumImage(null);
        setDetectedCenters(null);
        setManualNotchCenters([]);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSpectrumClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (autoDetect || !spectrumImage) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const img = event.currentTarget.querySelector('img');
    if (!img) return;

    // Get click position relative to the image
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Calculate actual image coordinates (scale from displayed size to actual size)
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;

    const actualX = Math.round(x * scaleX);
    const actualY = Math.round(y * scaleY);

    // Add to manual notch centers
    setManualNotchCenters(prev => [...prev, [actualX, actualY]]);
  };

  const clearManualNotches = () => {
    setManualNotchCenters([]);
  };

  const applyFilter = async () => {
    if (!originalImage) {
      setError('Please upload an image first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const base64Image = originalImage.split(',')[1];

      const requestData: any = {
        image: base64Image,
        filter_type: filterType,
        D0: D0,
        order: order
      };

      if (filterType === 'band') {
        requestData.bandWidth = bandWidth;
      } else {
        requestData.auto_detect = autoDetect;
        if (!autoDetect && manualNotchCenters.length > 0) {
          requestData.notch_centers = manualNotchCenters;
        }
      }

      const response = await axios.post(
        'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework5_q2',
        requestData,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 60000
        }
      );

      if (response.data.success) {
        setFilteredImage(`data:image/png;base64,${response.data.processed_image}`);
        setFilterVisualization(`data:image/png;base64,${response.data.filter_image}`);
        setSpectrumImage(`data:image/png;base64,${response.data.spectrum_image}`);
        
        if (response.data.notch_centers) {
          setDetectedCenters(response.data.notch_centers);
        }
      }
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hw4-question-container">
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="content-wrapper">
        <Link to="/homework/5" className="back-button">
          ← Back to Homework 5
        </Link>

        <header className="question-header">
          <div className="badge">Question 2</div>
          <h1>Notch & Band Reject Filters</h1>
          <p className="subtitle">Remove periodic noise and moiré patterns using frequency domain filtering</p>
        </header>

        <div className="algorithm-info">
          <h3>Butterworth Notch & Band Reject Filters</h3>
          <p>
            Periodic noise appears as bright spikes in the frequency domain spectrum. 
            Two types of filters can remove this noise:
          </p>
          <ul>
            <li>
              <strong>Notch Reject Filter:</strong> Removes specific frequency components by placing 
              "notches" (circular rejection zones) at noise spike locations. Requires identifying noise 
              spike positions in the frequency spectrum, either automatically or manually.
            </li>
            <li>
              <strong>Band Reject Filter:</strong> Removes a circular band of frequencies centered at 
              origin (DC component). Useful for removing noise that appears in a ring pattern around 
              the center of the frequency spectrum.
            </li>
          </ul>
          <p>
            Both filters use the Butterworth function for smooth frequency transitions, 
            minimizing ringing artifacts in the spatial domain.
          </p>
        </div>

        <div className="controls-section">
          <div className="control-group">
            <label htmlFor="filter-type">Filter Type:</label>
            <select
              id="filter-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as 'notch' | 'band')}
              className="window-select"
            >
              <option value="notch">Notch Reject Filter</option>
              <option value="band">Band Reject Filter</option>
            </select>
          </div>

          <div className="control-group">
            <label htmlFor="D0">
              {filterType === 'notch' ? 'Notch Radius (D₀):' : 'Inner Radius (D₀):'}
            </label>
            <input
              id="D0"
              type="range"
              min="5"
              max="50"
              value={D0}
              onChange={(e) => setD0(Number(e.target.value))}
              className="gamma-slider"
            />
            <span className="gamma-value">{D0}</span>
          </div>

          {filterType === 'band' && (
            <div className="control-group">
              <label htmlFor="bandwidth">Band Width (W):</label>
              <input
                id="bandwidth"
                type="range"
                min="10"
                max="100"
                value={bandWidth}
                onChange={(e) => setBandWidth(Number(e.target.value))}
                className="gamma-slider"
              />
              <span className="gamma-value">{bandWidth}</span>
            </div>
          )}

          <div className="control-group">
            <label htmlFor="order">Filter Order (n):</label>
            <select
              id="order"
              value={order}
              onChange={(e) => setOrder(Number(e.target.value))}
              className="window-select"
            >
              <option value={2}>2</option>
              <option value={4}>4</option>
              <option value={6}>6</option>
              <option value={8}>8</option>
            </select>
          </div>

          {filterType === 'notch' && (
            <>
              <div className="control-group">
                <label htmlFor="auto-detect">Auto-detect Notches:</label>
                <input
                  id="auto-detect"
                  type="checkbox"
                  checked={autoDetect}
                  onChange={(e) => {
                    setAutoDetect(e.target.checked);
                    if (e.target.checked) {
                      setManualNotchCenters([]);
                    }
                  }}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
              </div>

              {!autoDetect && (
                <div style={{
                  background: '#f0f4ff',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  border: '2px solid #667eea'
                }}>
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: '#667eea' }}>📍 Manual Notch Selection</strong>
                    <p style={{ margin: '5px 0', fontSize: '0.9em', color: '#666' }}>
                      Click on the Spectrum image below to select notch centers
                    </p>
                  </div>
                  {manualNotchCenters.length > 0 && (
                    <div>
                      <div style={{ marginBottom: '8px', fontSize: '0.9em' }}>
                        <strong>Selected Centers ({manualNotchCenters.length}):</strong>
                      </div>
                      <div style={{ marginBottom: '10px' }}>
                        {manualNotchCenters.map((center, idx) => (
                          <span key={idx} style={{
                            display: 'inline-block',
                            background: 'white',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            marginRight: '8px',
                            marginBottom: '5px',
                            border: '1px solid #667eea',
                            fontFamily: 'monospace',
                            fontSize: '0.85em'
                          }}>
                            ({center[0]}, {center[1]})
                            <button
                              onClick={() => setManualNotchCenters(prev => prev.filter((_, i) => i !== idx))}
                              style={{
                                marginLeft: '8px',
                                background: 'none',
                                border: 'none',
                                color: '#c33',
                                cursor: 'pointer',
                                fontSize: '1em',
                                padding: '0'
                              }}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={clearManualNotches}
                        style={{
                          padding: '6px 15px',
                          background: '#ff4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85em',
                          fontWeight: '600'
                        }}
                      >
                        Clear All
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="upload-area">
            <label htmlFor="image-upload" className="upload-label">
              <span className="upload-icon">📁</span>
              <span>Choose Image</span>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
            <button
              onClick={applyFilter}
              disabled={!originalImage || loading}
              className="process-button"
            >
              {loading ? 'Processing...' : 'Apply Filter'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {detectedCenters && (
            <div style={{
              background: '#e7f3ff',
              padding: '15px',
              borderRadius: '8px',
              marginTop: '15px',
              border: '2px solid #667eea'
            }}>
              <strong style={{ color: '#667eea', display: 'block', marginBottom: '8px' }}>
                🎯 Detected Notch Centers:
              </strong>
              <div style={{ 
                fontSize: '0.9em',
                fontFamily: 'monospace',
                color: '#333',
                lineHeight: '1.6'
              }}>
                {Array.isArray(detectedCenters) && detectedCenters.map((center: any, idx: number) => (
                  <span key={idx} style={{ 
                    marginRight: '15px',
                    display: 'inline-block',
                    background: 'white',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    marginBottom: '5px',
                    border: '1px solid #ccc'
                  }}>
                    ({center[0]}, {center[1]})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="images-grid-5">
          <div className="image-panel">
            <h3>Original Image</h3>
            <div className="image-container">
              {originalImage ? (
                <img src={originalImage} alt="Original" />
              ) : (
                <div className="placeholder">Upload an image</div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Spectrum (FFT) {filterType === 'notch' && !autoDetect && '(Click to select)'}</h3>
            <div 
              className="image-container"
              onClick={handleSpectrumClick}
              style={{
                cursor: filterType === 'notch' && !autoDetect && spectrumImage ? 'crosshair' : 'default',
                position: 'relative'
              }}
            >
              {spectrumImage ? (
                <>
                  <img src={spectrumImage} alt="Spectrum" />
                  {filterType === 'notch' && !autoDetect && manualNotchCenters.map((center, idx) => {
                    const img = document.querySelector('.image-container img') as HTMLImageElement;
                    const imgWidth = img?.naturalWidth || 512;
                    const imgHeight = img?.naturalHeight || 512;
                    return (
                      <div
                        key={idx}
                        style={{
                          position: 'absolute',
                          left: `${(center[0] / imgWidth) * 100}%`,
                          top: `${(center[1] / imgHeight) * 100}%`,
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: 'red',
                          border: '2px solid white',
                          transform: 'translate(-50%, -50%)',
                          pointerEvents: 'none',
                          boxShadow: '0 0 4px rgba(0,0,0,0.5)',
                          zIndex: 10
                        }}
                      />
                    );
                  })}
                </>
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Filter Visualization</h3>
            <div className="image-container">
              {filterVisualization ? (
                <img src={filterVisualization} alt="Filter" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Filtered Image</h3>
            <div className="image-container">
              {filteredImage ? (
                <img src={filteredImage} alt="Filtered" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="info-box">
          <h4>Parameter Guide</h4>
          <p>
            <strong>Notch Reject Filter:</strong><br/>
            • <strong>D₀ (Notch Radius):</strong> Size of each circular notch. Larger values reject more frequencies around each spike.<br/>
            • <strong>Auto-detect:</strong> Automatically finds bright spikes in the frequency spectrum and places notches.<br/>
            • <strong>Manual Centers:</strong> Specify exact (x,y) coordinates of noise spikes if auto-detection fails.<br/><br/>
            
            <strong>Band Reject Filter:</strong><br/>
            • <strong>D₀ (Inner Radius):</strong> Inner boundary of the rejected frequency band.<br/>
            • <strong>W (Band Width):</strong> Width of the rejection band. Outer radius = D₀ + W.<br/>
            • Rejects all frequencies between D₀ and D₀+W from the center.<br/><br/>
            
            <strong>Filter Order (n):</strong> Higher orders create sharper transitions but may introduce artifacts.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Homework5Question2;

